import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {Background} from '@mintdata/mint-kit/api/models/background/Background';
import {BackgroundType} from '@mintdata/mint-kit/api/models/background/BackgroundType';
import {BaseComponentModel} from '@mintdata/mint-kit/api/models/BaseComponentModel';
import {Border} from '@mintdata/mint-kit/api/models/Border';
import {BoxShadow} from '@mintdata/mint-kit/api/models/BoxShadow';
import {ColorWithVisibility} from '@mintdata/mint-kit/api/models/ColorWithVisibility';
import {ComponentState} from '@mintdata/mint-kit/api/models/componentState/ComponentState';
import {Font} from '@mintdata/mint-kit/api/models/Font';
import {Overflow} from '@mintdata/mint-kit/api/models/Overflow';
import {PositiveOffsets} from '@mintdata/mint-kit/api/models/PositiveOffsets';
import {TextAlign} from '@mintdata/mint-kit/api/models/TextAlign';
import {TextShadow} from '@mintdata/mint-kit/api/models/TextShadow';
import {TextVerticalAlign} from '@mintdata/mint-kit/api/models/TextVerticalAlign';
import {WordBreak} from '@mintdata/mint-kit/api/models/WordBreak';
import {BindingConfig} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {FieldPath} from '@mintdata/mint-kit/api/properties/BindPath';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {ABooleanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/ABooleanConfig';
import {AStringEnumConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AEnumConfig';
import {AStringConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AStringConfig';
import {BeanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BeanConfig';
import {FontConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/FontConfig';
import {PositiveOffsetsConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/PositiveOffsetsConfig';
import {TextAlignConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/TextAlignConfig';
import {TextVerticalAlignConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/TextVerticalAlignConfig';

export class TextModel extends BaseComponentModel
{
    @Property<AString>({
        title: 'Text',
        tip: 'Text',
        isRequired: false,
        typeConfig: AStringConfig,
        typeCtor: AString,
        binding: BindingConfig.with('@text[0]')
    })
    public text:AString;

    @Property<TextShadow>({
        title: 'Text Shadow',
        tip: 'Text Shadow',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: TextShadow
    })
    public textShadow?:TextShadow = TextShadow.default();

    @Property<TextShadow>({
        title: 'Text Shadow Hover',
        tip: 'Text Shadow Hover',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: TextShadow
    })
    public textShadowHover?:TextShadow = TextShadow.default();

    @Property<TextShadow>({
        title: 'Text Shadow Active',
        tip: 'Text Shadow Active',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: TextShadow
    })
    public textShadowActive?:TextShadow = TextShadow.default();

    @Property<Font>({
        title: 'Font',
        tip: 'Font',
        isRequired: false,
        typeConfig: FontConfig,
        typeCtor: Font
    })
    public font:Font;

    @Property<ABoolean>({
        title: 'Enable Text Selection',
        tip: 'Enable Text Selection',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean
    })
    allowTextSelection:ABoolean;

    @Property<ABoolean>({
        title: 'Force single line',
        tip: 'Force single line',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean
    })
    isSingleLine:ABoolean;

    @Property<ABoolean>({
        title: 'With Ellipsis',
        tip: 'With Ellipsis',
        isRequired: true,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean
    })
    withEllipsis:ABoolean;

    @Property<WordBreak>({
        title: 'Word Break',
        tip: 'Word Break',
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: WordBreak
    })
    wordBreak:WordBreak;

    @Property<Background>({
        title: 'Background',
        tip: 'Background',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Background
    })
    background:Background;

    @Property<Border>({
        title: 'Border',
        tip: 'Border',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: Border
    })
    border:Border;

    @Property<PositiveOffsets>({
        title: 'Padding',
        tip: 'Padding',
        category: PropertyCategory.Layout,
        isRequired: false,
        typeConfig: PositiveOffsetsConfig,
        typeCtor: PositiveOffsets
    })
    public padding?:PositiveOffsets = PositiveOffsets.fromEqualPixels(0);

    @Property<TextAlign>({
        title: 'Text Align',
        tip: 'Text Align',
        isRequired: false,
        typeConfig: TextAlignConfig,
        typeCtor: TextAlign
    })
    public textAlign?:TextAlign;

    @Property<TextVerticalAlign>({
        title: 'Vertical Align',
        tip: 'Vertical Align',
        isRequired: false,
        typeConfig: TextVerticalAlignConfig,
        typeCtor: TextVerticalAlign
    })
    public textVerticalAlign?:TextVerticalAlign;

    @Property<BoxShadow>({
        title: 'Shadows',
        tip: 'Shadows',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: BoxShadow
    })
    public boxShadow?:BoxShadow = BoxShadow.default();

    @Property<Overflow>({
        title: 'Horizontal overflow',
        tip: `Horizontal overflow`,
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: Overflow
    })
    overflowX:Overflow;

    @Property<Overflow>({
        title: 'Vertical overflow',
        tip: `Vertical overflow`,
        isRequired: true,
        typeConfig: AStringEnumConfig,
        typeCtor: Overflow
    })
    overflowY:Overflow;

    @Property<ComponentState>({
        title: 'Hover State',
        tip: 'Hover State',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: ComponentState
    })
    public hoverState?:ComponentState;

    @Property<ComponentState>({
        title: 'Active State',
        tip: 'Active State',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: ComponentState
    })
    public activeState?:ComponentState;

    public constructor(text:AString,
                       textShadow:TextShadow,
                       textShadowHover:TextShadow,
                       textShadowActive:TextShadow,
                       font:Font,
                       enableTextSelection:ABoolean,
                       isSingleLine:ABoolean,
                       withEllipsis:ABoolean,
                       wordBreak:WordBreak,
                       background:Background,
                       border:Border,
                       padding:PositiveOffsets,
                       textAlign:TextAlign,
                       textVerticalAlign:TextVerticalAlign,
                       boxShadow:BoxShadow,
                       overflowX:Overflow,
                       overflowY:Overflow,
                       hoverState:ComponentState,
                       activeState:ComponentState)
    {
        super();

        this.text = text;
        this.textShadow = textShadow;
        this.textShadowHover = textShadowHover;
        this.textShadowActive = textShadowActive;
        this.font = font;
        this.allowTextSelection = enableTextSelection;
        this.isSingleLine = isSingleLine;
        this.withEllipsis = withEllipsis;
        this.wordBreak = wordBreak;
        this.background = background;
        this.border = border;
        this.padding = padding;
        this.textAlign = textAlign;
        this.textVerticalAlign = textVerticalAlign;
        this.boxShadow = boxShadow;
        this.overflowX = overflowX;
        this.overflowY = overflowY;
        this.hoverState = hoverState;
        this.activeState = activeState;
    }

    public toString():string
    {
        return (this.text && this.font) ? `${this.text} ${this.font}` : null;
    }

    public static default():TextModel
    {
        const hoverState:ComponentState = new ComponentState();

        hoverState.isEnabled = ABoolean.false();
        hoverState.font = Font.default();
        hoverState.background = new Background(
            BackgroundType.solid(),
            ColorWithVisibility.whiteInvisible(),
            ColorWithVisibility.white(),
            ColorWithVisibility.black(),
            ANumber.fromNumber(0)
        );
        hoverState.border = Border.default();
        hoverState.boxShadow = BoxShadow.none();

        return new TextModel(
            AString.fromString('Text'),
            TextShadow.default(),
            TextShadow.default(),
            TextShadow.default(),
            Font.default(),
            ABoolean.false(), // enableTextSelection
            ABoolean.false(), // isSingleLine
            ABoolean.false(), // withEllipsis
            WordBreak.normal(),
            new Background(
                BackgroundType.solid(),
                ColorWithVisibility.whiteInvisible(),
                ColorWithVisibility.white(),
                ColorWithVisibility.black(),
                ANumber.fromNumber(0)
            ),
            Border.default(),
            PositiveOffsets.fromEqualPixels(0),
            TextAlign.left(),
            TextVerticalAlign.middle(),
            BoxShadow.none(),
            Overflow.hidden(),
            Overflow.hidden(),
            hoverState,
            hoverState
        );
    }

    public static exampleFrame():IFrame
    {
        return exampleFrame;
    }

    public static normalToHoverStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['textShadow', 'textShadowHover'],
            ['font', 'hoverState.font'],
            ['background', 'hoverState.background'],
            ['border', 'hoverState.border'],
            ['boxShadow', 'hoverState.boxShadow']
        ]);
    }

    public static normalToActiveStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['textShadow', 'textShadowActive'],
            ['font', 'activeState.font'],
            ['background', 'activeState.background'],
            ['border', 'activeState.border'],
            ['boxShadow', 'activeState.boxShadow']
        ]);
    }

    public static hoverToActiveStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['textShadowHover', 'textShadowActive'],
            ['hoverState.font', 'activeState.font'],
            ['hoverState.background', 'activeState.background'],
            ['hoverState.border', 'activeState.border'],
            ['hoverState.boxShadow', 'activeState.boxShadow']
        ]);
    }
}

const exampleFrame:IFrame = new AFrame(
    [
        new FrameField('text', Type.String)
    ],
    [
        [AString.fromString('Text')]
    ]
).extendWith(Font.exampleFrame());
